import {
    assertUnreachable,
    ChartKind,
    ChartSourceType,
    ResourceViewItemType,
    type ResourceViewChartItem,
    type ResourceViewItem,
} from '@lightdash/common';
import dayjs from 'dayjs';

export const getResourceTypeName = (item: ResourceViewItem) => {
    switch (item.type) {
        case ResourceViewItemType.DASHBOARD:
            return 'Dashboard';
        case ResourceViewItemType.SPACE:
            return 'Space';
        case ResourceViewItemType.DATA_APP:
            return 'Data app';
        case ResourceViewItemType.CHART:
            switch (item.data.chartKind) {
                case undefined:
                case ChartKind.VERTICAL_BAR:
                    return 'Bar chart';
                case ChartKind.HORIZONTAL_BAR:
                    return 'Horizontal bar chart';
                case ChartKind.LINE:
                    return 'Line chart';
                case ChartKind.SCATTER:
                    return 'Scatter chart';
                case ChartKind.AREA:
                    return 'Area chart';
                case ChartKind.MIXED:
                    return 'Mixed chart';
                case ChartKind.PIE:
                    return 'Pie chart';
                case ChartKind.FUNNEL:
                    return 'Funnel chart';
                case ChartKind.TREEMAP:
                    return 'Treemap';
                case ChartKind.GAUGE:
                    return 'Gauge';
                case ChartKind.TABLE:
                    return 'Table';
                case ChartKind.BIG_NUMBER:
                    return 'Big number';
                case ChartKind.CUSTOM:
                    return 'Custom visualization';
                case ChartKind.MAP:
                    return 'Map';
                case ChartKind.SANKEY:
                    return 'Sankey';
                default:
                    return assertUnreachable(
                        item.data.chartKind,
                        `Chart type ${item.data.chartKind} not supported`,
                    );
            }
        default:
            return assertUnreachable(item, 'Resource type not supported');
    }
};

const getChartResourceUrl = (
    projectUuid: string,
    item: ResourceViewChartItem,
) => {
    switch (item.data.source) {
        case ChartSourceType.SQL:
            return `/projects/${projectUuid}/sql-runner/${item.data.slug}`;
        case ChartSourceType.DBT_EXPLORE:
        case undefined:
            return `/projects/${projectUuid}/saved/${item.data.uuid}`;
        default:
            return assertUnreachable(
                item.data.source,
                `Unknown source type: ${item.data.source}`,
            );
    }
};

export const getResourceUrl = (projectUuid: string, item: ResourceViewItem) => {
    const itemType = item.type;
    switch (item.type) {
        case ResourceViewItemType.DASHBOARD:
            return `/projects/${projectUuid}/dashboards/${item.data.uuid}/view`;
        case ResourceViewItemType.CHART:
            return getChartResourceUrl(projectUuid, item);
        case ResourceViewItemType.SPACE:
            return `/projects/${projectUuid}/spaces/${item.data.uuid}`;
        case ResourceViewItemType.DATA_APP:
            return `/projects/${projectUuid}/apps/${item.data.uuid}/preview`;
        default:
            return assertUnreachable(item, `Can't get URL for ${itemType}`);
    }
};

export const getResourceName = (type: ResourceViewItemType) => {
    switch (type) {
        case ResourceViewItemType.DASHBOARD:
            return 'Dashboard';
        case ResourceViewItemType.CHART:
            return 'Chart';
        case ResourceViewItemType.SPACE:
            return 'Space';
        case ResourceViewItemType.DATA_APP:
            return 'Data app';
        default:
            return assertUnreachable(type, 'Resource type not supported');
    }
};

export const getResourceViewsSinceWhenDescription = (
    item: ResourceViewItem,
) => {
    if (
        item.type !== ResourceViewItemType.CHART &&
        item.type !== ResourceViewItemType.DASHBOARD &&
        item.type !== ResourceViewItemType.DATA_APP
    ) {
        throw new Error('Only supported for charts, dashboards and data apps');
    }

    return item.data.firstViewedAt
        ? `${item.data.views} views since ${dayjs(
              item.data.firstViewedAt,
          ).format('MMM D, YYYY h:mm A')}`
        : undefined;
};
